/**
 * reader-style-preset (= 副次 spec) の React hook。
 *
 * localStorage を single source of truth として preset を管理する。
 * - 初回 mount: localStorage から読込、 不在 / parse 失敗で DEFAULT_PRESET 採用
 * - updatePreset: 部分更新 + localStorage 即時保存 (= page reload 不要)
 * - resetPreset: DEFAULT_PRESET 復帰
 * - parse 失敗 / setItem 失敗で silent fallback (= try/catch、 UX 破壊しない)
 * - forward compat: unknown value で field 単位 default fallback
 *
 * 設計詳細は `.kiro/specs/reader-style-preset/design.md` を参照。
 */

import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_PRESET,
  STORAGE_KEY,
  type FlaggedContentVisibility,
  type ReaderFontSize,
  type ReaderPacing,
  type ReaderStylePreset,
  type ReaderTheme,
} from "./types";

const VALID_FONT_SIZES: readonly ReaderFontSize[] = [
  "small",
  "medium",
  "large",
];
const VALID_THEMES: readonly ReaderTheme[] = ["light", "dark"];
const VALID_PACINGS: readonly ReaderPacing[] = ["slow", "normal", "fast"];
const VALID_FLAGGED_VISIBILITY: readonly FlaggedContentVisibility[] = [
  "hide",
  "show",
];

function isValid<T extends string>(
  value: unknown,
  valid: readonly T[],
): value is T {
  return typeof value === "string" && (valid as readonly string[]).includes(value);
}

// parsed の各 field が valid なら採用、 不在 / 不正なら default fallback。
// 将来 field 追加時の forward compat はこの関数の拡張で対応。
function coerceToPreset(parsed: unknown): ReaderStylePreset {
  if (parsed === null || typeof parsed !== "object") {
    return DEFAULT_PRESET;
  }
  const p = parsed as Record<string, unknown>;
  return {
    fontSize: isValid(p.fontSize, VALID_FONT_SIZES)
      ? p.fontSize
      : DEFAULT_PRESET.fontSize,
    theme: isValid(p.theme, VALID_THEMES) ? p.theme : DEFAULT_PRESET.theme,
    pacing: isValid(p.pacing, VALID_PACINGS) ? p.pacing : DEFAULT_PRESET.pacing,
    flaggedContentVisibility: isValid(
      p.flaggedContentVisibility,
      VALID_FLAGGED_VISIBILITY,
    )
      ? p.flaggedContentVisibility
      : DEFAULT_PRESET.flaggedContentVisibility,
    // scene-beat-preview-toggle Phase 1: boolean field の forward compat fallback。
    // typeof check で true / false のみ採用、 不在 / 文字列 / number / null は default false。
    showBeatStructure:
      typeof p.showBeatStructure === "boolean"
        ? p.showBeatStructure
        : DEFAULT_PRESET.showBeatStructure,
  };
}

function readFromStorage(): ReaderStylePreset {
  if (typeof window === "undefined") return DEFAULT_PRESET;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_PRESET;
    return coerceToPreset(JSON.parse(raw));
  } catch {
    return DEFAULT_PRESET;
  }
}

function writeToStorage(preset: ReaderStylePreset): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preset));
  } catch {
    // quota exceeded / private mode 等で setItem 失敗時は silent fallback。
    // UX を破壊しない方針 (= preset は in-memory state で機能維持)。
  }
}

export interface UseReaderStylePresetResult {
  preset: ReaderStylePreset;
  updatePreset: (partial: Partial<ReaderStylePreset>) => void;
  resetPreset: () => void;
}

export function useReaderStylePreset(): UseReaderStylePresetResult {
  // initial state を lazy evaluation で localStorage 読込 (SSR safe)。
  const [preset, setPreset] = useState<ReaderStylePreset>(() =>
    readFromStorage(),
  );

  // mount 後に再読込: SSR initial 値と client 実値の差異対応。
  // (今回 client-only Vite SPA なので主に safety net 用途。)
  useEffect(() => {
    setPreset(readFromStorage());
  }, []);

  const updatePreset = useCallback(
    (partial: Partial<ReaderStylePreset>): void => {
      setPreset((prev) => {
        const next = { ...prev, ...partial };
        writeToStorage(next);
        return next;
      });
    },
    [],
  );

  const resetPreset = useCallback((): void => {
    setPreset(DEFAULT_PRESET);
    writeToStorage(DEFAULT_PRESET);
  }, []);

  return { preset, updatePreset, resetPreset };
}
